'use client'

import Mustache from 'mustache'
import React from 'react'

import { type CurrencyCode, type MoneyValue, Value } from '@mondough/money'

import { CopyContext } from './context'
import {
  BadPluralError,
  CopyNotFoundError,
  FatalCopyError,
  MissingCopyBundleError,
  UnrecognisedCopyError,
  VariableSubstitutionError,
} from './errors'
import { resolveVariables } from './formatters'
import type {
  CardinalForms,
  Components,
  CopyClient,
  CopyKey,
  CopyWithComponent,
  CopyWithVariable,
  DateFormatOptions,
  DateFormatStyle,
  DateTimeStyle,
  MoneyFormatOptions,
  OrdinalForms,
  PluralCopy,
  PluralType,
  PluralVariable,
  SimpleCopy,
  SupportedLocales,
  Variables,
} from './types'

/**
 * Maps each style-guide date style to its `Intl.DateTimeFormat` options.
 * The active locale drives month/weekday names, ordering and separators, so
 * no per-locale patterns are needed (mirrors the iOS `Date.FormatStyle`
 * style-guide formatters).
 */
const dateStyleToIntlOptions = (
  style: DateFormatStyle,
  { abbreviatedMonth = false, abbreviatedWeekday = false } = {},
): Intl.DateTimeFormatOptions => {
  const month: Intl.DateTimeFormatOptions['month'] = abbreviatedMonth
    ? 'short'
    : 'long'
  const weekday: Intl.DateTimeFormatOptions['weekday'] = abbreviatedWeekday
    ? 'short'
    : 'long'

  switch (style) {
    case 'shortDate':
      return { day: '2-digit', month: '2-digit', year: '2-digit' }
    case 'mediumDayMonthYear':
      return { day: 'numeric', month, year: 'numeric' }
    case 'mediumDayMonth':
      return { day: 'numeric', month }
    case 'longDayMonthYear':
      return { weekday, day: 'numeric', month, year: 'numeric' }
    case 'longDayMonth':
      return { weekday, day: 'numeric', month }
  }
}

// The clock (12h vs 24h) and hour-digit width are intentionally left to the
// locale: passing `hour: 'numeric'` lets Intl/CLDR pick the locale default
// (24-hour for en-GB/en-IE/es-ES, 12-hour with AM/PM for en-US).
const timeStyleToIntlOptions = (
  time: DateTimeStyle,
): Intl.DateTimeFormatOptions => {
  switch (time) {
    case 'shortTime':
      return { hour: 'numeric', minute: '2-digit' }
    case 'longTime':
      return { hour: 'numeric', minute: '2-digit', second: '2-digit' }
  }
}

/**
 * Container class for a plural variable
 */
class PluralVar implements PluralVariable {
  constructor(
    private name: string,
    private value: number,
    private rule: Intl.LDMLPluralRule = 'other',
  ) {}

  public get variable() {
    return { [this.name]: this.value }
  }

  public get pluralRule() {
    return this.rule
  }
}

type Args =
  | {
      key: CopyKey
      message: CopyWithVariable | CopyWithComponent
      variables?: Variables<CopyWithVariable>
      components?: Components<CopyWithComponent>
      preserveNewlines?: boolean
    }
  | {
      key: CopyKey
      message: CardinalForms | OrdinalForms
      plural: PluralVariable
      components?: Components<CopyWithComponent>
      preserveNewlines?: boolean
    }
  | { key: CopyKey; message: string; preserveNewlines?: boolean }

Mustache.escape = function (text) {
  return text
}

// scaffolded implementation to get a feel for how it would look/behave
export const useCopy = (): CopyClient => {
  const ctx = React.useContext(CopyContext)
  const { copy, locale, appName } = ctx

  if (!appName) {
    throw new Error(
      'useCopy can only be used inside a CopyProvider. Did you forget to wrap your app with <CopyProvider>?',
    )
  }

  // Memoize the copyClient based on the actual dependencies
  const copyClient = React.useMemo(() => {
    // The main copy function
    const client = ({ key, message, ...params }: Args) => {
      const { preserveNewlines = false } = params

      try {
        // no copy bundle available
        if (!copy) {
          throw new MissingCopyBundleError(ctx)
        }

        const copyValue = copy[key]

        // couldn't find copy for given key
        if (!copyValue) {
          throw new CopyNotFoundError(key)
        }

        const localisedCopy = copyValue.localized_messages[locale]

        // couldn't find copy for given locale
        if (!localisedCopy) {
          throw new CopyNotFoundError(key, locale)
        }

        // We've found the copy key in the bundle, it has a variant for
        // current locale, and it is a SIMPLE copy type
        if (isSimpleForm(localisedCopy)) {
          const simpleContent = localisedCopy.simple.content

          // We need to do some interpolation
          if ('variables' in params || 'components' in params) {
            const result = substituteVariables(simpleContent, params, locale)
            return preserveNewlines ? wrapWithNewlineStyle(result) : result
          }

          return preserveNewlines
            ? wrapWithNewlineStyle(simpleContent)
            : simpleContent
        }

        // We've found the copy key in the bundle, it has a variant for
        // current locale, and it is a PLURAL_FORMS copy type
        if (isPluralForm(localisedCopy)) {
          const pluralContent = localisedCopy.plural_forms

          // The params supplied match the form required for plurals
          if (isPluralParam(params)) {
            // Fallback to the *other* form if the desired is one not found
            const copyString =
              pluralContent[params.plural.pluralRule] ?? pluralContent.other

            const result =
              typeof copyString === 'string'
                ? substituteVariables(copyString, params, locale) // the 'other' form
                : substituteVariables(copyString.content, params, locale) // the desired form

            return preserveNewlines ? wrapWithNewlineStyle(result) : result
          } else {
            // We don't recognise the plural argument
            throw new BadPluralError(key, localisedCopy, params)
          }
        }

        // We've reached somewhere we never should
        throw new UnrecognisedCopyError(key, locale, localisedCopy, params)
      } catch (error) {
        // A copy resolution failure (MissingCopyBundleError, CopyNotFoundError,
        // BadPluralError, UnrecognisedCopyError, VariableSubstitutionError, or
        // an unexpected error). We recover via the inline fallback below, but
        // first surface it to the consuming app's reporter if wired (e.g.
        // Sentry). The console log stays dev-only to keep production and test
        // output clean (warn, since we recover via the fallback below).
        const err = error instanceof Error ? error : new Error(String(error))
        ctx.onError?.(err)
        if (process.env.NODE_ENV === 'development') {
          console.warn(err)
        }

        // Try to use fallback copy
        const fallbackResult = getFallbackCopy(key, locale, message, params)
        return preserveNewlines
          ? wrapWithNewlineStyle(fallbackResult)
          : fallbackResult
      }
    }

    client.string = (args: Args) => {
      if ('components' in args) {
        throw new Error(
          "ArgumentError: you're trying to return a string but have supplied components.",
        )
      }

      // Strip preserveNewlines to ensure we get a plain string, not JSX wrapped in span
      const { preserveNewlines, ...argsWithoutPreserveNewlines } = args
      const result = client(argsWithoutPreserveNewlines)

      if (typeof result !== 'string') {
        throw new Error(
          'TypeError: Expected result to be a string but got JSX.',
        )
      }

      return result
    }

    client.Cardinal = 'cardinal' as const
    client.Ordinal = 'ordinal' as const

    client.plural = (
      variable: string,
      value: number,
      type: PluralType = client.Cardinal,
    ): PluralVariable => {
      const pluralRules = new Intl.PluralRules(locale, { type })
      const pluralRule = pluralRules.select(value)
      return new PluralVar(variable, value, pluralRule)
    }

    client.cardinals = (forms: CardinalForms) => {
      return forms
    }

    client.ordinals = (forms: OrdinalForms) => {
      return forms
    }

    client.money = ((
      value: number | MoneyValue,
      currencyCodeOrOptions?: CurrencyCode | MoneyFormatOptions,
      options?: MoneyFormatOptions,
    ) => {
      let numericValue: number
      let currency: CurrencyCode
      let opts: MoneyFormatOptions | undefined

      // Handle Money.Value instances
      if (value instanceof Value) {
        // Second param can be options object or undefined (for backward compatibility)
        if (
          currencyCodeOrOptions !== undefined &&
          typeof currencyCodeOrOptions === 'string'
        ) {
          throw new Error(
            'When using Money.Value, pass options as the second parameter, not a currency code',
          )
        }
        opts =
          (currencyCodeOrOptions as MoneyFormatOptions | undefined) ?? options
        numericValue = value.majorUnitsDecimal.toNumber()
        currency = value.currencyCode as CurrencyCode
      } else {
        // Second param must be a currency code string
        if (typeof currencyCodeOrOptions !== 'string') {
          throw new Error(
            'currencyCode is required as the second parameter when passing a numeric value',
          )
        }
        currency = currencyCodeOrOptions
        numericValue = value
        opts = options
      }

      const {
        useGrouping = true,
        showSignWhenPositive = false,
        showSignWhenNegative = true,
        hideFractionalWhenZero = false,
        currencyDisplay = 'narrowSymbol',
        minimumFractionDigits,
        maximumFractionDigits,
        formatToParts = false,
      } = opts ?? {}

      // Determine if the value is a whole number for hideFractionalWhenZero
      const isWholeNumber = numericValue === Math.floor(numericValue)

      // Handle hideFractionalWhenZero: only hide decimals for whole numbers
      const minDigits =
        hideFractionalWhenZero && isWholeNumber ? 0 : minimumFractionDigits

      // Build the formatter options
      const formatterOptions: Intl.NumberFormatOptions = {
        style: 'currency',
        currency,
        useGrouping,
        currencyDisplay,
        signDisplay: 'never',
      }

      if (minDigits !== undefined) {
        formatterOptions.minimumFractionDigits = minDigits
      }
      if (maximumFractionDigits !== undefined) {
        formatterOptions.maximumFractionDigits = maximumFractionDigits
      }
      if (showSignWhenPositive && numericValue >= 0) {
        formatterOptions.signDisplay = 'always'
      }
      if (showSignWhenNegative && numericValue < 0) {
        formatterOptions.signDisplay = 'always'
      }

      const formatter = new Intl.NumberFormat(locale, formatterOptions)
      if (formatToParts) {
        return formatter.formatToParts(numericValue)
      }
      return formatter.format(numericValue)
    }) as CopyClient['money']

    client.formatDate = ((
      value: Date | number,
      options?: DateFormatOptions,
    ) => {
      const {
        style,
        time,
        abbreviatedMonth = false,
        abbreviatedWeekday = false,
        formatToParts = false,
      } = options ?? {}

      // Render the date unless the caller asked for time only (`time` set and
      // `style` omitted). Otherwise default the date style to mediumDayMonthYear.
      const dateStyle = style ?? (time ? undefined : 'mediumDayMonthYear')

      const intlOptions: Intl.DateTimeFormatOptions = {
        ...(dateStyle
          ? dateStyleToIntlOptions(dateStyle, {
              abbreviatedMonth,
              abbreviatedWeekday,
            })
          : {}),
        ...(time ? timeStyleToIntlOptions(time) : {}),
      }

      const formatter = new Intl.DateTimeFormat(locale, intlOptions)

      if (formatToParts) {
        return formatter.formatToParts(value)
      }
      return formatter.format(value)
    }) as CopyClient['formatDate']

    /**
     * Utility to mark a hardcoded string as copy
     *
     * This can be used to identify all copy that needs to be considered
     * for use of lib.copy. It is intended to be useful for both: humans
     * (to not miss any potential copy) and AI agents (to be quicker at
     * identifing copy).
     *
     * It is only intended to be used as part of a workflow for copy
     * migration, and it should eventually be replaced by an appropriate
     * usage of lib.copy.
     */
    client.TODO = (copy: string) => {
      return copy
    }

    return client
  }, [copy, locale, ctx]) // Only recreate when these values change

  return copyClient
}

// private helpers

/**
 * Parses a string containing component tags and converts them to React elements.
 * Supports nested components using a stack-based parser.
 *
 * @example
 * parseComponentString(
 *   "Visit <link><bold>our site</bold></link>",
 *   {
 *     link: ({ children }) => <a href="#">{children}</a>,
 *     bold: ({ children }) => <strong>{children}</strong>
 *   }
 * )
 * // Returns: <>Visit <a href="#"><strong>our site</strong></a></>
 */
function parseComponentString(
  text: string,
  components: Components<CopyWithComponent>,
): JSX.Element {
  const componentNames = Object.keys(components)
  let keyCounter = 0

  // Token types for our parser
  type Token =
    | { type: 'text'; value: string }
    | { type: 'open'; name: string }
    | { type: 'close'; name: string }
    | { type: 'self-closing'; name: string }

  // Tokenize the input string
  const tokens: Token[] = []
  let i = 0

  while (i < text.length) {
    // Look for opening tag
    if (text[i] === '<') {
      const tagEnd = text.indexOf('>', i)
      if (tagEnd === -1) {
        // No closing >, treat as text
        tokens.push({ type: 'text', value: text.slice(i) })
        break
      }

      const tagContent = text.slice(i + 1, tagEnd)

      // Check for self-closing tag
      if (tagContent.endsWith('/')) {
        const tagName = tagContent.slice(0, -1).trim()
        if (componentNames.includes(tagName)) {
          tokens.push({ type: 'self-closing', name: tagName })
          i = tagEnd + 1
          continue
        }
      }

      // Check for closing tag
      if (tagContent.startsWith('/')) {
        const tagName = tagContent.slice(1).trim()
        if (componentNames.includes(tagName)) {
          tokens.push({ type: 'close', name: tagName })
          i = tagEnd + 1
          continue
        }
      }

      // Check for opening tag
      const spaceIndex = tagContent.indexOf(' ')
      const tagName =
        spaceIndex === -1 ? tagContent : tagContent.slice(0, spaceIndex)
      if (componentNames.includes(tagName.trim())) {
        tokens.push({ type: 'open', name: tagName.trim() })
        i = tagEnd + 1
        continue
      }

      // Not a recognized component tag, treat as text
      const nextChar = text[i]
      if (nextChar !== undefined) {
        tokens.push({ type: 'text', value: nextChar })
      }
      i++
    } else {
      // Collect text until next tag
      let textEnd = text.indexOf('<', i)
      if (textEnd === -1) {
        textEnd = text.length
      }
      tokens.push({ type: 'text', value: text.slice(i, textEnd) })
      i = textEnd
    }
  }

  // Parse tokens into a tree structure using a stack
  type Node =
    | { type: 'text'; value: string }
    | { type: 'component'; name: string; children: Node[] }

  const parseTokens = (tokens: Token[]): Node[] => {
    const root: Node[] = []
    const stack: { name: string; children: Node[] }[] = []
    let current = root

    for (const token of tokens) {
      if (token.type === 'text') {
        if (token.value) {
          current.push({ type: 'text', value: token.value })
        }
      } else if (token.type === 'open') {
        const node = { name: token.name, children: [] as Node[] }
        current.push({ type: 'component', ...node })
        stack.push(node)
        current = node.children
      } else if (token.type === 'close') {
        const topOfStack = stack[stack.length - 1]
        if (topOfStack && topOfStack.name === token.name) {
          stack.pop()
          const newTop = stack[stack.length - 1]
          current = newTop ? newTop.children : root
        }
        // If tag doesn't match, ignore it (malformed HTML)
      } else if (token.type === 'self-closing') {
        current.push({ type: 'component', name: token.name, children: [] })
      }
    }

    return root
  }

  // Convert nodes to React elements
  const nodesToReact = (nodes: Node[]): (string | JSX.Element)[] => {
    const result: (string | JSX.Element)[] = []

    for (const node of nodes) {
      if (node.type === 'text') {
        result.push(node.value)
      } else if (node.type === 'component') {
        const component = components[node.name]
        if (typeof component === 'function') {
          const children =
            node.children.length > 0 ? nodesToReact(node.children) : undefined
          const element = component({ children })
          result.push(React.cloneElement(element, { key: keyCounter++ }))
        }
      }
    }

    return result
  }

  const tree = parseTokens(tokens)
  const reactElements = nodesToReact(tree)

  return <>{reactElements}</>
}

function getFallbackCopy(
  key: CopyKey,
  locale: SupportedLocales,
  fallback: string | CardinalForms | OrdinalForms,
  params: {
    plural?: PluralVariable
    variables?: Variables<string> | PluralVariable
    components?: Components<string>
    preserveNewlines?: boolean
  },
) {
  try {
    // static string fallback, i.e. no params
    if (
      !params.variables &&
      !params.components &&
      typeof fallback === 'string'
    ) {
      return fallback
    }

    // we have params, so do interpolation
    // simple form
    if (typeof fallback === 'string') {
      return substituteVariables(fallback, params, locale)
    }

    // plural form
    if (isPluralParam(params)) {
      const fallbackPlural = fallback[params.plural.pluralRule]

      return fallbackPlural
        ? substituteVariables(fallbackPlural, params, locale)
        : substituteVariables(fallback.other, params, locale)
    } else {
      throw new BadPluralError(key, fallback, params)
    }

    throw new FatalCopyError(key, locale, fallback, params)
  } catch (err) {
    if (err instanceof FatalCopyError) {
      throw err
    } else {
      // we can't return any copy given the data provided, so fail loud
      throw new FatalCopyError(key, locale, fallback, params, err)
    }
  }
}

function substituteVariables(
  copy: string,
  params: {
    plural?: PluralVariable
    variables?: Variables<CopyWithVariable> | PluralVariable
    components?: Components<CopyWithComponent>
  },
  locale: SupportedLocales,
) {
  let copyTemplate = copy
  // A string will be returned if there are no components used in
  // substitution. If there are, then a ReactFragment will be returned
  let result: string | JSX.Element = ''

  try {
    // Deal with regular string substitution first, either simple variables
    // or a plural form
    if (params.plural) {
      copyTemplate = Mustache.render(copyTemplate, params.plural.variable)
      result = copyTemplate
    } else if (params.variables) {
      const resolved = resolveVariables(
        params.variables as Record<string, unknown>,
        locale,
      )
      copyTemplate = Mustache.render(copyTemplate, resolved)
      result = copyTemplate
    }

    // Now replace any referenced components
    if (params.components) {
      /*
        Given the input string
        <emphasise>{{ greeting }}</emphasise>, have a <emphasise>{{ adjective }}</emphasise> day
        with the string substitution already having happened above, using
        { greeting: 'Howdy partner', adjective: 'special'}
        the string after substitution will be:
        <emphasise>Howdy partner</emphasise>, have a <emphasise>special</emphasise> day

        We parse the string to find all component tags and convert them to React elements.
        This parser supports nested components like:
        <link><bold>text</bold></link>

        The parser uses a stack-based approach to handle nesting properly.
      */
      result = parseComponentString(copyTemplate, params.components)
    }

    return result
  } catch (err) {
    throw new VariableSubstitutionError(copy, params, err)
  }
}

/**
 * Wraps content with white-space: pre-line styling to preserve newlines
 */
function wrapWithNewlineStyle(
  content: string | JSX.Element,
): string | JSX.Element {
  return <span style={{ whiteSpace: 'pre-line' }}>{content}</span>
}

function isSimpleForm(copy: SimpleCopy | PluralCopy): copy is SimpleCopy {
  return copy !== undefined && copy.type === 'SIMPLE'
}

function isPluralForm(copy: SimpleCopy | PluralCopy): copy is PluralCopy {
  return copy !== undefined && copy.type === 'PLURAL_FORMS'
}

function isPluralParam(params: {
  plural?: PluralVariable
  variables?: Variables<string>
  components?: Components<string>
  preserveNewlines?: boolean
}): params is {
  plural: PluralVariable
  components?: Components<string>
  preserveNewlines?: boolean
} {
  return 'plural' in params && params.plural instanceof PluralVar
}
